import { LegalLayout } from '../../../components/layout/LegalLayout';

export default function CookiesPage() {
  return (
    <LegalLayout
      title="Politique de cookies"
      url="https://solutionecofrance.fr/legal/cookies"
      lastUpdated="25/05/2026"
    >
      <p>
        La présente politique décrit l&rsquo;usage que Solution Éco France fait des cookies et
        autres traceurs sur le site <strong>solutionecofrance.fr</strong>. Elle est conforme à
        l&rsquo;article 82 de la loi Informatique et Libertés, aux lignes directrices et à la
        recommandation de la <strong>CNIL</strong> du 17 septembre 2020 (mises à jour successives
        2022-2024) et au Règlement (UE) 2016/679 (RGPD).
      </p>

      <h2>1. Qu&rsquo;est-ce qu&rsquo;un cookie&nbsp;?</h2>
      <p>
        Un cookie est un petit fichier texte déposé sur votre terminal (ordinateur, tablette,
        smartphone) lors de la consultation d&rsquo;un site web. Il permet à l&rsquo;éditeur du
        site, ou à un tiers, de reconnaître votre terminal lors de visites ultérieures, de mémoriser
        des préférences ou de mesurer l&rsquo;audience du site. La notion de cookie inclut, au sens
        de la CNIL, l&rsquo;ensemble des traceurs techniquement assimilables&nbsp;: pixels, balises
        web, identifiants stockés en <em>localStorage</em>, fingerprinting, etc.
      </p>

      <h2>2. Catégories de cookies utilisés</h2>

      <h3>2.1. Cookies strictement nécessaires (sans consentement)</h3>
      <p>
        Ces cookies sont indispensables au fonctionnement du site. Ils ne peuvent pas être
        désactivés et sont exemptés de consentement préalable conformément à l&rsquo;article 82 de
        la LIL.
      </p>
      <ul>
        <li>
          <strong>sef_session_id</strong> — identifiant de session anonyme (stocké en{' '}
          <em>sessionStorage</em>). Finalité&nbsp;: cohérence tracking, attribution et journal de
          consentement. Durée&nbsp;: session de navigation (effacé à la fermeture de
          l&rsquo;onglet).
        </li>
        <li>
          <strong>sef_lead_id</strong> — reconnaissance d&rsquo;un visiteur ayant déjà soumis une
          demande, pour éviter de redemander les mêmes informations. Durée&nbsp;: 1 an.{' '}
          <em>HttpOnly, Secure, SameSite=Lax</em>.
        </li>
        <li>
          <strong>sef_csrf</strong> — jeton de protection contre la falsification de requêtes
          inter-sites. Durée&nbsp;: session. <em>HttpOnly, Secure</em>.
        </li>
        <li>
          <strong>sef_consent</strong> — mémorisation de votre choix relatif aux traceurs.
          Durée&nbsp;: 6 mois (recommandation CNIL).
        </li>
        <li>
          <strong>sef_audience</strong> — mémorisation de votre profil sélectionné (particulier,
          professionnel, artisan&hellip;) pour personnaliser la navigation. Durée&nbsp;: 30 jours.
        </li>
      </ul>

      <h3>2.2. Cookies de mesure d&rsquo;audience (avec consentement)</h3>
      <p>
        Ces cookies nous permettent de mesurer l&rsquo;audience du site et d&rsquo;améliorer
        l&rsquo;expérience utilisateur. Ils ne sont déposés qu&rsquo;après votre consentement
        explicite via le bandeau, sauf lorsque la configuration retenue répond aux critères
        d&rsquo;exemption de la CNIL (anonymisation forte, absence de croisement, durée ≤ 13 mois).
      </p>
      <ul>
        <li>
          <strong>PostHog</strong> (région UE) — événements anonymisés (pages vues, clics,
          conversions agrégées), absence de profilage individuel, pas de croisement avec
          d&rsquo;autres bases. Durée des cookies&nbsp;: 13 mois max&nbsp;; durée des événements
          stockés&nbsp;: 25 mois max.
        </li>
        <li>
          <strong>Google Analytics 4 (GA4)</strong> via Google Tag Manager — mesure d&rsquo;audience
          agrégée (pages vues, sessions, sources de trafic). Les cookies <code>_ga</code>,{' '}
          <code>_ga_*</code> sont déposés uniquement après consentement. Durée&nbsp;: 13 mois.
          Responsable de traitement&nbsp;: Google Ireland Limited.{' '}
          <a href="https://policies.google.com/privacy" rel="noopener noreferrer" target="_blank">
            Politique de confidentialité Google
          </a>
          .
        </li>
        <li>
          <strong>Google Tag Manager</strong> — chargé avec <strong>Consent Mode v2</strong> (tous
          consentements à «&nbsp;refusé&nbsp;» par défaut). Aucune donnée n&rsquo;est collectée
          avant votre accord. GTM permet de piloter l&rsquo;ensemble des balises de mesure et de
          marketing depuis une interface centralisée, tout en respectant vos choix.
        </li>
      </ul>

      <h3>2.3. Cookies marketing et publicitaires (avec consentement)</h3>
      <p>
        Ces cookies permettent de mesurer l&rsquo;efficacité de nos campagnes publicitaires et, le
        cas échéant, d&rsquo;afficher des publicités personnalisées sur les réseaux sociaux et les
        moteurs de recherche. Ils ne sont activés qu&rsquo;après votre consentement explicite.
      </p>
      <ul>
        <li>
          <strong>Meta Pixel (Facebook / Instagram)</strong> — suivi des conversions issues de nos
          campagnes publicitaires&nbsp;: identifie les actions réalisées sur le site après un clic
          sur une annonce (prise de contact, simulation d&rsquo;aides, diagnostic). Durée&nbsp;:
          jusqu&rsquo;à 180 jours. Responsable de traitement&nbsp;: Meta Platforms Ireland Limited.{' '}
          <a
            href="https://www.facebook.com/privacy/explanation"
            rel="noopener noreferrer"
            target="_blank"
          >
            Politique de confidentialité Meta
          </a>
          .
        </li>
      </ul>
      <p>
        En cas de refus des cookies marketing, vos données ne sont pas transmises à des fins
        publicitaires. Les éventuelles publicités affichées sur les réseaux sociaux restent
        génériques et non ciblées.
      </p>

      <h3>2.4. Cookies tiers déposés par contenus embarqués</h3>
      <p>
        Certains contenus tiers (vidéos, cartes interactives) peuvent déposer des cookies sur votre
        terminal lorsqu&rsquo;ils sont chargés&nbsp;:
      </p>
      <ul>
        <li>
          <strong>OpenStreetMap / Leaflet</strong> (cartographie) — aucun cookie déposé, requêtes
          uniquement.
        </li>
        <li>
          <strong>Vidéos embarquées</strong> (le cas échéant) — chargées en mode sans cookie quand
          le fournisseur le permet (YouTube nocookie).
        </li>
      </ul>

      <h2>3. Recueil et gestion du consentement</h2>
      <p>Conformément aux recommandations CNIL, notre bandeau de consentement vous permet&nbsp;:</p>
      <ul>
        <li>
          d&rsquo;<strong>accepter</strong> tous les traceurs en un clic&nbsp;;
        </li>
        <li>
          de <strong>refuser</strong> tous les traceurs en un clic, avec la même facilité que pour
          accepter&nbsp;;
        </li>
        <li>
          de <strong>personnaliser</strong> finement votre choix par finalité&nbsp;;
        </li>
        <li>
          de <strong>modifier</strong> votre choix à tout moment via le lien{' '}
          <em>«&nbsp;Gérer mes cookies&nbsp;»</em> en pied de page.
        </li>
      </ul>
      <p>
        L&rsquo;absence de réponse de votre part équivaut à un refus. Votre choix est conservé
        pendant 6 mois maximum, après quoi il vous sera redemandé.
      </p>

      <h2>4. Conséquences d&rsquo;un refus</h2>
      <p>
        Le refus des cookies non strictement nécessaires n&rsquo;a{' '}
        <strong>
          aucun impact sur l&rsquo;accès au site ni sur l&rsquo;utilisation de nos services
        </strong>
        . Seules les fonctionnalités de mesure d&rsquo;audience et de personnalisation publicitaire
        seront désactivées.
      </p>

      <h2>5. Réglages du navigateur</h2>
      <p>
        Vous pouvez également configurer votre navigateur pour bloquer ou supprimer les
        cookies&nbsp;:
      </p>
      <ul>
        <li>
          <a
            href="https://support.google.com/chrome/answer/95647"
            rel="noopener noreferrer"
            target="_blank"
          >
            Google Chrome
          </a>
        </li>
        <li>
          <a
            href="https://support.mozilla.org/fr/kb/protection-renforcee-contre-pistage-firefox-ordinateur"
            rel="noopener noreferrer"
            target="_blank"
          >
            Mozilla Firefox
          </a>
        </li>
        <li>
          <a
            href="https://support.apple.com/fr-fr/guide/safari/sfri11471/mac"
            rel="noopener noreferrer"
            target="_blank"
          >
            Apple Safari
          </a>
        </li>
        <li>
          <a
            href="https://support.microsoft.com/fr-fr/microsoft-edge"
            rel="noopener noreferrer"
            target="_blank"
          >
            Microsoft Edge
          </a>
        </li>
      </ul>

      <h2>6. Évolution de la politique</h2>
      <p>
        Cette politique peut être mise à jour pour refléter les évolutions réglementaires ou
        techniques. Toute modification substantielle déclenchera une nouvelle présentation du
        bandeau de consentement.
      </p>

      <h2>7. Contact</h2>
      <p>
        Pour toute question relative aux cookies ou à l&rsquo;exercice de vos droits, contactez
        notre DPO&nbsp;: <a href="mailto:dpo@solutionecofrance.fr">dpo@solutionecofrance.fr</a>.
      </p>
    </LegalLayout>
  );
}
